import React, { useState, useEffect } from 'react';
import { Layout as AntLayout, Dropdown, Space, Avatar, Typography } from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  WalletOutlined,
  BellOutlined,
  SettingOutlined,
  DownOutlined,
  SafetyCertificateOutlined,
  HomeOutlined,
  ShopOutlined,
  BankOutlined,
  SwapOutlined,
  AppstoreOutlined,
  ThunderboltOutlined,
  HeartOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useHomepageVersion } from '../contexts/HomepageVersionContext';
import NotificationBell from './NotificationBell';
import AiChatWidget from './AiChatWidget';
import LanguageSwitcher from './LanguageSwitcher';
import api from '../services/api';

const { Content } = AntLayout;
const { Text } = Typography;

const GOLD = '#B8892A';
const BG = '#FAFAF8';
const BD = '#E8E0D0';
// ICP 备案号:北京云川数贸科技有限公司已在工信部为 ycvintage.com 备案(阿里云备案管理确认 2026-07-08)。
const ICP_NUMBER = '京ICP备2021037660号-7';

export default function Layout({ children, hideHeader = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isLoggedIn } = useAuth();
  const { t } = useTranslation();
  const homepageVersion = useHomepageVersion();
  const isProxy = homepageVersion === 'consignment';

  const [exhEnabled, setExhEnabled] = useState(null);
  const [valEnabled, setValEnabled] = useState(false);
  useEffect(() => {
    api.get('/exhibitions/flags').then(r => {
      setExhEnabled(r.data?.exhibition_enabled && r.data?.exhibition_public_list);
    }).catch(() => setExhEnabled(false));
    api.get('/lots/valuation-flags').then(r => {
      setValEnabled(!!r.data?.valuation_nav_visible);
    }).catch(() => setValEnabled(false));
  }, []);

  // 收藏 tab 角标：登录后统计 24h 内即将结拍的收藏件数（拉回用户的关键提醒）
  const [watchSoonCount, setWatchSoonCount] = useState(0);
  useEffect(() => {
    if (!isLoggedIn) { setWatchSoonCount(0); return; }
    let alive = true;
    const load = () => api.get('/watchlist').then(r => {
      const items = r.data?.data || r.data || [];
      const now = Date.now();
      const H24 = 24 * 3600 * 1000;
      const n = (Array.isArray(items) ? items : []).filter(it => {
        const end = it.end_at || it.session_end;
        if (!end) return false;
        const tEnd = new Date(end).getTime();
        return tEnd > now && tEnd - now <= H24;
      }).length;
      if (alive) setWatchSoonCount(n);
    }).catch(() => {});
    load();
    const id = setInterval(load, 120000); // 每 2 分钟刷新
    return () => { alive = false; clearInterval(id); };
  }, [isLoggedIn]);

  // Auction sub-menu items for the dropdown
  const auctionDropdownItems = [
    { key: '/auction-hub', label: t('nav.browse') },
    { key: '/auction-intro#houses', label: t('nav.auctionHouses'), anchor: true },
  ];

  const isHomepage = location.pathname === '/';
  // 导航项固定 — 与是否在首页/是否登录无关,避免跳转首页时导航条数量变化、位置抖动
  const navItems = isProxy ? [
    { key: '/', label: t('nav.home') },
    { key: '/auction-hub', label: t('tabs.auctionHub') },
    { key: '/bidding-center', label: t('landingProxy.navCalendar') },
    ...(valEnabled ? [{ key: '/valuation', label: t('nav.valuation') }] : []),
    { key: '/help/bidding-guide', label: t('landingProxy.navGuide') },
  ] : [
    { key: '/', label: t('nav.home') },
    { key: '/market', label: t('nav.market') },
    ...(exhEnabled ? [{ key: '/exhibitions', label: t('nav.exhibition') }] : []),
    { key: '/consignment', label: t('nav.consignment') },
    ...(valEnabled ? [{ key: '/valuation', label: t('nav.valuation') }] : []),
    { key: 'auction-dropdown', label: t('nav.auction'), children: auctionDropdownItems },
  ];

  // Flat list for selectedKey matching (includes dropdown children)
  const allNavKeys = navItems.flatMap(item => item.children ? item.children.filter(c => !c.anchor).map(c => c.key) : [item.key]);

  // Mobile bottom tab bar items
  const bottomTabs = isProxy ? [
    { key: '/', label: t('tabs.home'), icon: <HomeOutlined /> },
    { key: '/auction-hub', label: t('tabs.auctionHub'), icon: <AppstoreOutlined /> },
    { key: '/bidding-center', label: t('landingProxy.navCalendar'), icon: <ThunderboltOutlined />, center: true },
    { key: '/watchlist', label: t('tabs.watchlist'), icon: <HeartOutlined />, badge: watchSoonCount },
    { key: '/my-center', label: t('tabs.my'), icon: <UserOutlined /> },
  ] : [
    { key: '/', label: t('tabs.home'), icon: <HomeOutlined /> },
    { key: '/auction-hub', label: t('tabs.auctionHub'), icon: <AppstoreOutlined /> },
    { key: '/market', label: t('tabs.market'), icon: <ShopOutlined /> },
    { key: '/consignment', label: t('tabs.consignment'), icon: <SwapOutlined /> },
    { key: '/my-center', label: t('tabs.my'), icon: <UserOutlined /> },
  ];

  const selectedKey = allNavKeys.find(
    (k) => k !== '/' && location.pathname.startsWith(k)
  ) || (location.pathname === '/' ? '/' : '');

  // Check if current page is under auction dropdown (full version only)
  const isAuctionActive = !isProxy && auctionDropdownItems.some(
    item => item.key !== '/' && location.pathname.startsWith(item.key)
  );

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isKycVerified = user?.kyc_status === 'verified';
  const userMenuItems = [
    {
      key: 'profile',
      label: t('userMenu.profile'),
      icon: <UserOutlined />,
      onClick: () => navigate('/profile', { state: { section: 'overview' } }),
    },
    ...(isKycVerified ? [] : [{
      key: 'kyc',
      label: t('userMenu.kyc'),
      icon: <SafetyCertificateOutlined />,
      onClick: () => navigate('/profile', { state: { section: 'kyc' } }),
    }]),
    {
      key: 'deposit',
      label: t('userMenu.deposit'),
      icon: <WalletOutlined />,
      onClick: () => navigate('/deposit'),
    },
    {
      key: 'notifications',
      label: t('userMenu.notifications'),
      icon: <BellOutlined />,
      onClick: () => navigate('/notifications'),
    },
    {
      key: 'settings',
      label: t('userMenu.settings'),
      icon: <SettingOutlined />,
      onClick: () => navigate('/settings'),
    },
    { type: 'divider' },
    {
      key: 'logout',
      label: t('auth.logout'),
      icon: <LogoutOutlined />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  return (
    <AntLayout style={{ minHeight: '100vh', background: BG }}>
      {/* Header */}
      {!hideHeader && <div
        style={{
          background: '#fff',
          boxShadow: '0 1px 0 ' + BD + ', 0 2px 8px rgba(17,14,10,0.05)',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          borderBottom: '1px solid ' + BD,
        }}
      >
        {/* Top row: Logo + User */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          maxWidth: 1200,
          margin: '0 auto',
          padding: isMobile ? '0 16px' : '0 32px',
          height: 60,
        }}>
          {/* Logo */}
          <Link to="/" style={{ textDecoration: 'none', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 4, background: '#8C1F1F', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F5E6D0', fontSize: 10, fontWeight: 700, lineHeight: 1.1, textAlign: 'center', letterSpacing: 1 }}>云川<br/>中古</div>
            {!isMobile && (
              <span style={{ fontFamily: 'var(--serif)', fontSize: 18, color: GOLD, letterSpacing: 4, lineHeight: 1 }}>
                {t('brand.name')}
              </span>
            )}
          </Link>

          {/* Desktop Nav (hidden on mobile) */}
          {!isMobile && (
            <nav style={{ display: 'flex', gap: 2, flex: 1, justifyContent: 'center' }}>
              {navItems.map((item) => {
                if (item.children) {
                  // Dropdown menu item
                  const dropdownMenuItems = item.children.map(child => ({
                    key: child.key,
                    label: child.label,
                    onClick: () => {
                      if (child.anchor) {
                        const [path, hash] = child.key.split('#');
                        navigate(path);
                        setTimeout(() => {
                          document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      } else {
                        navigate(child.key);
                      }
                    },
                  }));
                  const isDropdownActive = isAuctionActive;
                  return (
                    <Dropdown key={item.key} menu={{ items: dropdownMenuItems }} trigger={['hover']}>
                      <button
                        onClick={() => navigate('/auction-hub')}
                        style={{
                          padding: '6px 16px',
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: 13,
                          color: isDropdownActive ? GOLD : '#6B5E4E',
                          fontFamily: 'var(--sans)',
                          borderBottom: isDropdownActive ? `2px solid ${GOLD}` : '2px solid transparent',
                          transition: 'all 0.15s',
                          fontWeight: isDropdownActive ? 500 : 400,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                        onMouseEnter={(e) => { if (!isDropdownActive) e.currentTarget.style.color = '#110E0A'; }}
                        onMouseLeave={(e) => { if (!isDropdownActive) e.currentTarget.style.color = '#6B5E4E'; }}
                      >
                        {item.label} <DownOutlined style={{ fontSize: 9 }} />
                      </button>
                    </Dropdown>
                  );
                }
                const active = selectedKey === item.key;
                return (
                  <button
                    key={item.key}
                    onClick={() => navigate(item.key)}
                    style={{
                      padding: '6px 16px',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: 13,
                      color: active ? GOLD : '#6B5E4E',
                      fontFamily: 'var(--sans)',
                      borderBottom: active ? `2px solid ${GOLD}` : '2px solid transparent',
                      transition: 'all 0.15s',
                      fontWeight: active ? 500 : 400,
                    }}
                    onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = '#110E0A'; }}
                    onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = '#6B5E4E'; }}
                  >
                    {item.label}
                  </button>
                );
              })}
            </nav>
          )}

          {/* Right side */}
          <div style={{ flexShrink: 0, marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
            <LanguageSwitcher compact={isMobile} />
            {isLoggedIn && <NotificationBell />}
            {isLoggedIn ? (
              <Dropdown menu={{ items: userMenuItems }} trigger={['click']} placement="bottomRight">
                <Space size={4} style={{ cursor: 'pointer', padding: '4px 10px', border: '1px solid ' + BD, borderRadius: 20 }}>
                  <Avatar size={28} icon={<UserOutlined />} style={{ background: GOLD }} />
                  {/* 用户名手机端也显示(用户要求"显示个姓")。有姓名优先显示姓名,否则手机号尾号4位。 */}
                  <Text style={{ maxWidth: isMobile ? 72 : 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13, color: '#110E0A' }}>
                    {user?.name || (user?.phone ? String(user.phone).slice(-4) : '')}
                  </Text>
                  <DownOutlined style={{ fontSize: 10, color: '#A8998A' }} />
                </Space>
              </Dropdown>
            ) : (
              <Space size={isMobile ? 6 : 8}>
                <button
                  onClick={() => navigate('/login')}
                  style={{
                    padding: isMobile ? '6px 12px' : '7px 18px',
                    background: 'transparent',
                    border: '1px solid ' + BD,
                    borderRadius: 3,
                    cursor: 'pointer',
                    fontSize: 13,
                    color: '#6B5E4E',
                    fontFamily: 'var(--sans)',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = GOLD; e.currentTarget.style.color = GOLD; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = BD; e.currentTarget.style.color = '#6B5E4E'; }}
                >
                  {t('auth.login')}
                </button>
                <button
                  onClick={() => navigate('/register')}
                  style={{
                    padding: isMobile ? '6px 12px' : '7px 18px',
                    background: GOLD,
                    border: '1px solid ' + GOLD,
                    borderRadius: 3,
                    cursor: 'pointer',
                    fontSize: 13,
                    color: '#fff',
                    fontFamily: 'var(--sans)',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#A07A22'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = GOLD; }}
                >
                  {t('auth.register')}
                </button>
              </Space>
            )}
          </div>
        </div>

        {/* Mobile Nav row (全功能版のみ) */}
        {isMobile && !isProxy && (
          <nav style={{
            display: 'flex',
            overflowX: 'auto',
            overflowY: 'hidden',
            borderTop: '1px solid ' + BD,
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
          }}>
            <style>{`nav::-webkit-scrollbar { display: none; }`}</style>
            {navItems.flatMap(item => item.children ? item.children.filter(c => !c.anchor) : [item]).map((item) => {
              const active = selectedKey === item.key;
              return (
                <button
                  key={item.key}
                  onClick={() => navigate(item.key)}
                  style={{
                    flexShrink: 0,
                    padding: '8px 12px',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: active ? `2px solid ${GOLD}` : '2px solid transparent',
                    cursor: 'pointer',
                    fontSize: 13,
                    color: active ? GOLD : '#6B5E4E',
                    fontFamily: 'var(--sans)',
                    fontWeight: active ? 600 : 400,
                    whiteSpace: 'nowrap',
                    transition: 'all 0.15s',
                  }}
                >
                  {item.label}
                </button>
              );
            })}
          </nav>
        )}
      </div>}

      {/* paddingBottom 交给下方 footer(其底部已 72px 避让底部导航条),Content 不再额外留 60px,消除页脚上方大空白 */}
      <Content style={{ background: BG, minHeight: `calc(100vh - ${isMobile && !hideHeader ? 96 : hideHeader ? 0 : 60}px - 60px)`, paddingBottom: 0 }}>
        {children}
      </Content>

      {/* Mobile Bottom Tab Bar */}
      {isMobile && (
        <nav style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
          background: '#fff', borderTop: '1px solid ' + BD,
          display: 'flex', justifyContent: 'space-around', alignItems: 'center',
          height: 56, paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          boxShadow: '0 -2px 8px rgba(0,0,0,.04)',
        }}>
          {bottomTabs.map(tab => {
            const active = tab.key === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(tab.key);
            if (tab.center) {
              return (
                <button key={tab.key} onClick={() => navigate(tab.key)} style={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                  gap: 1, padding: 0, background: 'none', border: 'none', cursor: 'pointer',
                  position: 'relative', marginTop: -18,
                }}>
                  <div style={{
                    width: 48, height: 48, borderRadius: '50%',
                    background: active ? 'linear-gradient(135deg, #D4A030, #B8892A)' : 'linear-gradient(135deg, #C8922A, #A07A22)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: active ? '0 2px 12px rgba(184,137,42,.5)' : '0 2px 8px rgba(184,137,42,.3)',
                    color: '#fff', fontSize: 24, lineHeight: 1,
                  }}>{tab.icon}</div>
                  <span style={{ fontSize: 10, fontWeight: 600, color: active ? GOLD : '#999', marginTop: 1 }}>{tab.label}</span>
                </button>
              );
            }
            return (
              <button key={tab.key} onClick={() => navigate(tab.key)} style={{
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: 2, padding: '6px 0', background: 'none', border: 'none', cursor: 'pointer',
                color: active ? GOLD : '#999',
              }}>
                <span style={{ fontSize: 20, lineHeight: 1, position: 'relative' }}>
                  {tab.icon}
                  {tab.badge > 0 && (
                    <span style={{
                      position: 'absolute', top: -6, left: 12,
                      minWidth: 15, height: 15, padding: '0 4px', borderRadius: 8,
                      background: '#C0392B', color: '#fff', fontSize: 10, lineHeight: '15px',
                      fontWeight: 600, textAlign: 'center', boxSizing: 'border-box',
                    }}>{tab.badge > 99 ? '99+' : tab.badge}</span>
                  )}
                </span>
                <span style={{ fontSize: 10, fontWeight: active ? 600 : 400 }}>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      )}

      {/* 客服微信悬浮入口(左下,避让右下 AI 客服;客服页本身不再显示) */}
      {location.pathname !== '/support' && (
        <button
          onClick={() => navigate('/support')}
          aria-label={t('support.title')}
          style={{
            position: 'fixed', left: 16, bottom: isMobile ? 74 : 24, zIndex: 900,
            width: 46, height: 46, borderRadius: '50%', border: 'none', cursor: 'pointer',
            background: 'linear-gradient(135deg, #1A6B54, #14543f)', color: '#fff',
            boxShadow: '0 4px 14px rgba(26,107,84,.4)', fontSize: 20,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >🎧</button>
      )}

      {/* AI Chat Widget — 客户要求移除悬浮客服按钮(2026-07) */}
      {/* <AiChatWidget /> */}

      {/* Footer \u2014 \u516c\u53f8\u4e3b\u4f53/\u4e1a\u52a1\u8bf4\u660e\u5408\u89c4\u5c55\u793a\u3002\u624b\u673a\u7aef\u4e5f\u663e\u793a(\u5fae\u4fe1\u652f\u4ed8 H5 \u5ba1\u6838\u5458\u7528\u79fb\u52a8\u7aef\u6d4f\u89c8,
          \u5fc5\u987b\u80fd\u770b\u5230\u8fd0\u8425\u4e3b\u4f53\u300c\u5317\u4eac\u4e91\u5ddd\u6570\u8d38\u79d1\u6280\u6709\u9650\u516c\u53f8\u300d+ \u4e1a\u52a1\u573a\u666f\u300c\u65e5\u672c\u4e2d\u53e4\u5546\u54c1\u62cd\u5356\u4ee3\u62cd\u670d\u52a1\u300d)\u3002 */}
      <footer
        style={{
          textAlign: 'center',
          background: '#F2EBD9',
          color: '#6B5E4E',
          padding: isMobile ? '18px 20px calc(72px + env(safe-area-inset-bottom, 0px))' : '20px 32px',
          borderTop: '1px solid ' + BD,
          fontSize: 12,
          fontFamily: 'var(--mono)',
          letterSpacing: 0.5,
          lineHeight: 1.9,
        }}
      >
        {/* \u26a0\ufe0f \u4e2d\u6587\u5fc5\u987b\u5305\u8fdb JS \u5b57\u7b26\u4e32 {'...'};\u672c\u6587\u4ef6 ASCII \u5b58\u50a8\u4f1a\u628a\u4e2d\u6587\u8f6c\u6210 \uXXXX,
            JSX \u6807\u7b7e\u95f4\u7684\u88f8\u6587\u672c\u4e0d\u89e3\u6790 \u \u8f6c\u4e49\u4f1a\u539f\u6837\u663e\u793a\u6210\u4e71\u7801(2026-07-08 \u5b9e\u6d4b),\u5b57\u7b26\u4e32\u91cc\u624d\u4f1a\u89e3\u6790\u3002 */}
        <div style={{ fontWeight: 600, color: '#4A4030' }}>{'\u5317\u4eac\u4e91\u5ddd\u6570\u8d38\u79d1\u6280\u6709\u9650\u516c\u53f8'}</div>
        <div>
          {'\u00a9'} {new Date().getFullYear()} {'\u4e91\u5ddd\u4e2d\u53e4 YCVINTAGE'}
          {ICP_NUMBER ? (
            <>
              {' \u00b7 '}
              <a href="https://beian.miit.gov.cn/" target="_blank" rel="noreferrer" style={{ color: '#6B5E4E', textDecoration: 'none' }}>{ICP_NUMBER}</a>
            </>
          ) : null}
        </div>
      </footer>
    </AntLayout>
  );
}
